// src/pages/motolivraison/commercant-detail.page.jsx
//
// Fiche d'un commerçant MotoLivraison : profil, statistiques et boutiques rattachées.
// Le commerçant est un User porteur d'un `commercantProfile` ; ses boutiques sont
// les documents Commerce dont il est `ownerId`.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Button, message, Space, Breadcrumb,
  Avatar, Spin, Table, Empty, Typography,
} from 'antd';
import {
  UserOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined, ShopOutlined, EyeOutlined,
  ContainerOutlined, DollarOutlined, PhoneOutlined, MailOutlined,
  ClockCircleOutlined, InfoCircleOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte,
} from './table.helpers';
import './css/motolivraison-table.css';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

const CommercantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commercant, setCommercant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commerces, setCommerces] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(false);

  const fetchCommercant = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommercantById(id);
      setCommercant(data);
    } catch (e) {
      message.error(e?.message || 'Erreur lors du chargement du commerçant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCommercant(); }, [fetchCommercant]);

  // Boutiques du commerçant (filtre serveur par propriétaire)
  const fetchCommerces = useCallback(async () => {
    setLoadingCommerces(true);
    try {
      const r = await motolivraisonService.listCommerces({ ownerId: id, limit: 1000 });
      setCommerces(r?.commerces || []);
    } catch (e) {
      /* silencieux : le tableau affiche un état vide */
    } finally {
      setLoadingCommerces(false);
    }
  }, [id]);

  useEffect(() => { fetchCommerces(); }, [fetchCommerces]);

  const handleAction = async (action) => {
    try {
      if (action === 'validate') await motolivraisonService.validateCommercant(id);
      else if (action === 'suspend') await motolivraisonService.suspendCommercant(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommercant(id);
      message.success('Action effectuée');
      fetchCommercant();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !commercant) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const profil = commercant.commercantProfile || {};
  const nomComplet = `${commercant.firstName || ''} ${commercant.lastName || ''}`.trim() || '—';

  const colonnesCommerces = [
    {
      title: 'Logo', key: 'logo', width: 80, align: 'center',
      render: (_, r) => (
        <Avatar size={40} src={r.logo} icon={<ShopOutlined />} style={{ backgroundColor: '#fa8c16' }} />
      ),
    },
    {
      title: 'Boutique', dataIndex: 'nom', key: 'nom', width: 200, ellipsis: true,
      sorter: (a, b) => cmpTexte(a.nom, b.nom),
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Catégorie', key: 'categorie', width: 170, ellipsis: true,
      sorter: (a, b) => cmpTexte(a.categorieId?.nom, b.categorieId?.nom),
      render: (_, r) => r.categorieId?.nom || '—',
    },
    {
      title: 'Ville', key: 'ville', width: 130,
      sorter: (a, b) => cmpTexte(a.cityId?.name, b.cityId?.name),
      render: (_, r) => r.cityId?.name || '—',
    },
    {
      title: 'Adresse', dataIndex: 'adresse', key: 'adresse', width: 200, ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Statut', dataIndex: 'status', key: 'status', width: 130,
      filters: ['pending', 'approved', 'suspended', 'rejected'].map(v => ({
        text: ({ rejected: 'Rejeté' })[v] || statusLabel(v), value: v,
      })),
      onFilter: (value, r) => r.status === value,
      render: (s) => <Tag color={s === 'rejected' ? 'default' : statusColor(s)}>
        {s === 'rejected' ? 'Rejeté' : statusLabel(s)}
      </Tag>,
    },
    {
      title: 'Actions', key: 'act', width: 130, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          style={BTN_ACTION(COULEURS_ACTION.voir)}
          onClick={() => navigate(`/dashboard/motolivraison/commerces/${r._id}`)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commercants">Commerçants</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{nomComplet}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── Bannière : on identifie tout de suite le commerçant ─────── */}
        <Card style={{ marginBottom: 16, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
          <div
            style={{
              padding: '22px 24px',
              background: 'linear-gradient(135deg, #334155, #475569)',
              color: '#fff',
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between" wrap>
              <Col xs={24} lg={15}>
                <Space size={18} align="start" wrap={false} style={{ width: '100%' }}>
                  <Avatar
                    size={84}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#fa8c16', border: '3px solid rgba(255,255,255,.85)', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 24, lineHeight: 1.2 }}>{nomComplet}</h2>
                    <Space size={[6, 6]} wrap style={{ marginTop: 8 }}>
                      <Tag color={statusColor(profil.status)} style={{ margin: 0 }}>{statusLabel(profil.status)}</Tag>
                      <Tag color={commercant.isActive ? 'green' : 'red'} style={{ margin: 0 }}>
                        {commercant.isActive ? 'Compte actif' : 'Compte désactivé'}
                      </Tag>
                      <Tag style={{ margin: 0 }}>{commerces.length} boutique{commerces.length > 1 ? 's' : ''}</Tag>
                    </Space>
                    <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                      <PhoneOutlined style={{ marginRight: 6 }} />
                      {formatPhoneCI(commercant.phone)}
                      {commercant.email && (
                        <span style={{ marginLeft: 14 }}><MailOutlined style={{ marginRight: 6 }} />{commercant.email}</span>
                      )}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>
                      <ClockCircleOutlined style={{ marginRight: 6 }} />
                      Inscrit {profil.registeredAt ? `le ${dayjs(profil.registeredAt).format('DD/MM/YYYY')}` : '—'}
                    </div>
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
                  {profil.status === 'pending' && (
                    <Button icon={<CheckCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                      onClick={() => handleAction('validate')}>
                      Approuver
                    </Button>
                  )}
                  {profil.status === 'approved' && (
                    <Button icon={<StopOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                      onClick={() => handleAction('suspend')}>
                      Suspendre
                    </Button>
                  )}
                  {profil.status === 'suspended' && (
                    <Button icon={<PlayCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                      onClick={() => handleAction('reactivate')}>
                      Réactiver
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {/* Chiffres clés du commerçant */}
          <Row style={{ borderTop: '1px solid #f0f0f0' }}>
            {[
              { label: 'Boutiques', valeur: fmt(commerces.length), couleur: '#fa8c16', icone: <ShopOutlined /> },
              { label: 'Commandes', valeur: fmt(profil.stats?.totalCommandes), couleur: '#1677ff', icone: <ContainerOutlined /> },
              { label: 'Revenus brut', valeur: `${fmt(profil.stats?.totalRevenusBrut)} F`, couleur: '#334155', icone: <DollarOutlined /> },
              { label: 'Revenus net', valeur: `${fmt(profil.stats?.totalRevenusNet)} F`, couleur: '#52c41a', icone: <DollarOutlined /> },
            ].map((k, i) => (
              <Col xs={12} md={6} key={k.label} style={{ padding: '14px 18px', borderLeft: i ? '1px solid #f0f0f0' : 'none' }}>
                <Space size={10} align="center">
                  <span style={{ background: `${k.couleur}1a`, color: k.couleur, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {k.icone}
                  </span>
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{k.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: k.couleur }}>{k.valeur}</div>
                  </div>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* ─── Colonne principale : ses boutiques ────────────────────── */}
          <Col xs={24} xl={17}>
            <Card
              size="small"
              title={<Space><ShopOutlined />Boutiques ({commerces.length})</Space>}
              extra={<Link to="/dashboard/motolivraison/commerces">Toutes les boutiques</Link>}
            >
              <Table
                className="moto-table"
                rowKey="_id"
                size="middle"
                loading={loadingCommerces}
                dataSource={commerces}
                locale={{ ...LOCALE_TABLE, emptyText: <Empty description="Ce commerçant n'a aucune boutique" /> }}
                pagination={PAGINATION_TABLE('boutique')}
                scroll={{ x: 1040 }}
                columns={colonnesCommerces}
              />
            </Card>
          </Col>

          {/* ─── Colonne latérale : identité et suivi du compte ────────── */}
          <Col xs={24} xl={7}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card size="small" title={<Space><PhoneOutlined />Contact</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 90 }}>
                  <Descriptions.Item label="Téléphone">{formatPhoneCI(commercant.phone)}</Descriptions.Item>
                  <Descriptions.Item label="Email">{commercant.email || '—'}</Descriptions.Item>
                </Descriptions>
                <Button block icon={<PhoneOutlined />} href={`tel:${commercant.phone}`}>
                  Appeler le commerçant
                </Button>
              </Card>

              <Card size="small" title={<Space><InfoCircleOutlined />Compte</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 110 }}>
                  <Descriptions.Item label="Statut">
                    <Tag color={statusColor(profil.status)}>{statusLabel(profil.status)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Inscrit le">
                    {profil.registeredAt ? dayjs(profil.registeredAt).format('DD/MM/YYYY à HH:mm') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approuvé le">
                    {profil.approvedAt ? dayjs(profil.approvedAt).format('DD/MM/YYYY à HH:mm') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Compte actif">
                    <Tag color={commercant.isActive ? 'green' : 'red'}>{commercant.isActive ? 'Oui' : 'Non'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Rôle principal">{commercant.role || '—'}</Descriptions.Item>
                </Descriptions>
                {profil.documentUrl && (
                  <Button block icon={<FileTextOutlined />} href={profil.documentUrl} target="_blank" rel="noreferrer">
                    Voir la pièce justificative
                  </Button>
                )}
              </Card>

              <Card size="small" title={<Space><ShopOutlined />Répartition des boutiques</Space>}>
                {commerces.length ? (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {['approved', 'pending', 'suspended', 'rejected'].map(st => {
                      const n = commerces.filter(c => c.status === st).length;
                      if (!n) return null;
                      const label = st === 'rejected' ? 'Rejetée' : statusLabel(st);
                      return (
                        <div key={st} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <Tag color={st === 'rejected' ? 'default' : statusColor(st)} style={{ margin: 0 }}>{label}</Tag>
                          <strong>{n}</strong>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 8, borderTop: '1px dashed #f0f0f0' }}>
                      <span style={{ color: '#8c8c8c' }}>Villes couvertes</span>
                      <strong>{new Set(commerces.map(c => c.cityId?.name).filter(Boolean)).size}</strong>
                    </div>
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Aucune boutique enregistrée pour l'instant.
                  </Text>
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CommercantDetail;
